import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import { Translate } from '../../../shared/LanguageService';
import { sanitizeAgreementHtml } from '../../../shared/agreementHtml';
import { useBrokerAgreementQuery } from './queries/useBrokerAgreementQuery';
import { ShownAgreement } from './types';

type BrokerAgreementDialogProps = {
    brokerAgentId: number;
    brokerFirmId: number;
    planOnly: boolean;
    onClose: () => void;
    onShown: (shown: ShownAgreement) => void;
};

function AgreementSkeleton() {
    return (
        <Box role="progressbar" aria-label={Translate('orders/create/agreement/loading')}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
        </Box>
    );
}

export function BrokerAgreementDialog({
    brokerAgentId,
    brokerFirmId,
    planOnly,
    onClose,
    onShown
}: BrokerAgreementDialogProps) {
    const { data, isError } = useBrokerAgreementQuery(
        brokerAgentId,
        brokerFirmId,
        planOnly,
        (agreement) =>
            onShown({
                customerAgreementVersionId: agreement.customerAgreementVersionId,
                templateVersionId: agreement.templateVersionId
            })
    );

    function renderContent() {
        if (isError) {
            return (
                <Alert severity="error">{Translate('orders/create/agreement/load-error')}</Alert>
            );
        }

        if (!data) {
            return <AgreementSkeleton />;
        }

        return <div dangerouslySetInnerHTML={{ __html: sanitizeAgreementHtml(data.content) }} />;
    }

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{Translate('orders/create/agreement-modal/title')}</DialogTitle>
            <DialogContent dividers>{renderContent()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {Translate('orders/create/agreement-modal/button-labels/close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
